import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import CustomMaskedInput from "./CustomMaskedInput";
import * as yup from "yup";
import { useFormik } from "formik";
import useAuth from "../../hooks/useAuthContext";

const InputText = ({ 
  id, 
  // label, 
  mask, 
  placeholder, 
  value, 
  // onChange, 
  // onBlur, 
  required, 
  disabled, 
  maxLength,
  hint,
  validationFunction,
  showValidation = true,
  // type = "text",
  className = "",
  ...props 
}) => {
  // Gerar ID único se não fornecido
  const uniqueId = useMemo(() => {
    return id || `input-${Math.random().toString(36).substr(2, 9)}`;
  }, [id]);
  const { validarEmail } = useAuth();
  const [isValidating, setIsValidating] = useState(false);
  const [isValid, setIsValid] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [classErro, setClassErro] = useState("");
  const [erroEmail, setErroEmail] = useState(false);
  const inputRef = useRef(null);

  // Esquema de validação memoizado
  const schema = useMemo(() => {
    let tipo = props.type || "text";
    let schemaBase;

    switch (tipo) {
      case "text":
        schemaBase = yup.string();
        break;
      case "email":
        schemaBase = yup.string().email("E-mail inválido");
        break;
      case "date":
        schemaBase = yup.date("Data inválida");
        break;
      case "number":
        schemaBase = yup.number("Campo inválido");
        break;
      default:
        schemaBase = yup.string();
        break;
    }

    if (props.requered) {
      schemaBase = schemaBase.required("Este campo é obrigatório");
    }

    if (props.countCPF) {
      schemaBase = schemaBase.test(
        'cpf-length',
        'CPF deve ter 11 dígitos',
        (value) => {
          if (!value) return !props.requered;
          const numbers = value.replace(/\D/g, '');
          return numbers.length === 11;
        }
      );
    }

    if (props.mascara === "99/99/9999") {
      schemaBase = yup.string().test(
        'date-format',
        'Data inválida',
        (value) => {
          if (!value) return !props.requered;
          const numbers = value.replace(/\D/g, '');
          if (numbers.length !== 8) return false;
          
          const day = parseInt(numbers.substring(0, 2));
          const month = parseInt(numbers.substring(2, 4));
          const year = parseInt(numbers.substring(4, 8));
          
          if (day < 1 || day > 31) return false;
          if (month < 1 || month > 12) return false;
          if (year < 1900 || year > new Date().getFullYear()) return false;
          
          // Verificar se a data é válida
          const date = new Date(year, month - 1, day);
          return date.getDate() === day && 
                 date.getMonth() === month - 1 && 
                 date.getFullYear() === year;
        }
      );
    }

    if (props.minLength) {
      schemaBase = schemaBase.min(props.minLength, `Mínimo de ${props.minLength} caracteres`);
    }

    if (props.maxLength) {
      schemaBase = schemaBase.max(props.maxLength, `Máximo de ${props.maxLength} caracteres`);
    }

    return { valor: schemaBase };
  }, [props.type, props.requered, props.countCPF, props.minLength, props.maxLength]);

  const formik = useFormik({
    initialValues: {
      valor: props.value || "",
    },
    validationSchema: yup.object(schema),
    validateOnChange: true,
    validateOnBlur: true,
  });

  // Sincronizar valor externo com formik
  useEffect(() => {
    if (props.value !== formik.values.valor) {
      formik.setFieldValue('valor', props.value || '');
    }
  }, [props.value]);

  // Função de limpeza de caracteres da máscara
  const limparMascara = useCallback((valor) => {
    if (!props.mascara) return valor;
    return valor
      .replace(/[_\.\-\(\)\s]/g, "")
      .trim();
  }, [props.mascara]);

  // Handler de mudança otimizado
  const handleChange = useCallback((e) => {
    let valor = e.target.value;

    const limpouErroDuplicado =
      props.validaEmail && props.type === 'email' ? true : false;
    if (limpouErroDuplicado) {
      setErroEmail(false);
    }

    // Para inputs com máscara, o valor já vem limpo do CustomMaskedInput
    if (!props.mascara && props.financeiro) {
      valor = valor.replace(/\D/g, '');
    }

    // Atualizar formik
    formik.handleChange({
      target: {
        name: 'valor',
        value: valor
      }
    });

    const erroDuplicadoEmail = limpouErroDuplicado ? false : erroEmail;
    const hasError =
      formik.touched.valor && (formik.errors.valor || erroDuplicadoEmail);
    setClassErro(hasError ? "input-erro" : "");

    if (props.onChange) {
      if (props.validaEmail && props.type === 'email') {
        props.onChange(valor);
      } else {
        props.onChange(hasError ? "" : valor);
      }
    }

    // Chamar validador do formulário
    if (props.validador) {
      setTimeout(() => {
        props.validador();
      }, 0);
    }
  }, [formik, props.onChange, props.validador, erroEmail, props.mascara, props.financeiro, props.label, props.validaEmail, props.type]);

  // Verificação de e-mail já cadastrado (API) ao sair do campo
  const handleBlur = useCallback(
    async (e) => {
      // O <input> usa name={uniqueId}, mas o Formik só conhece o campo 'valor'.
      // handleBlur nativo quebraria o touched/validate; não usar e.target.name aqui.
      const valor = e.target.value;
      await formik.setFieldTouched('valor', true, false);
      const fieldError = await formik.validateField('valor');

      let emailJaCadastrado = false;
      if (props.validaEmail && props.type === 'email') {
        if (!valor.trim() || fieldError) {
          setErroEmail(false);
        } else {
          try {
            setIsValidating(true);
            const existe = await validarEmail(valor.trim());
            emailJaCadastrado = !!existe;
            setErroEmail(emailJaCadastrado);
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Erro ao verificar e-mail:', err);
            setErroEmail(false);
          } finally {
            setIsValidating(false);
          }
        }
      }

      const hasFormatError = !!fieldError;
      const hasError = hasFormatError || emailJaCadastrado;
      setClassErro(hasError ? 'input-erro' : '');

      if (props.onChange) {
        if (props.validaEmail && props.type === 'email') {
          props.onChange(valor);
        } else {
          props.onChange(hasError ? '' : valor);
        }
      }

      if (props.validador) {
        props.validador();
      }
    },
    [formik, props.onChange, props.validador, props.validaEmail, props.type, validarEmail]
  );

  // Renderização condicional do input
  const renderInput = () => {
    const commonProps = {
      id: uniqueId,
      name: uniqueId,
      autoFocus: props.focus ?? false,
      value: formik.values.valor,
      className: `form-control form-control-lg ${classErro}`,
      disabled: props.disabled || false,
      placeholder: props.placeholder || "",
      ref: inputRef,
      'aria-describedby': formik.errors.valor || erroEmail ? `${uniqueId}-error` : undefined,
      'aria-invalid': !!(formik.errors.valor || erroEmail),
    };

    if (props.mascara) {
      // Mapear máscaras para o formato do CustomMaskedInput
      let maskType = props.mascara;
      if (props.mascara === "999.999.999-99") maskType = "cpf";
      else if (props.mascara === "(99) 99999-9999") maskType = "telefone";
      else if (props.mascara === "99999-999") maskType = "cep";
      else if (props.mascara === "99/99/9999") maskType = "data";



      return (
        <CustomMaskedInput
          {...commonProps}
          mask={maskType}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      );
    }

    if (props.financeiro) {
      return (
        <input
          {...commonProps}
          type="number"
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={props.placeholder || "R$ 0,00"}
          step="0.01"
          min="0"
        />
      );
    }

    return (
      <input
        {...commonProps}
        type={props.type || "text"}
        onChange={handleChange}
        onBlur={handleBlur}
      />
    );
  };

  return (
    <div className={`${props.classNames || "mb-1"} input-wrapper ${
      formik.touched.valor && formik.errors.valor ? 'has-error' : ''
    } ${
      formik.touched.valor && !formik.errors.valor && !erroEmail && formik.values.valor ? 'has-success' : ''
    } ${formik.touched.valor ? 'has-focus' : ''}`}>
      <label 
        htmlFor={uniqueId} 
        className={`form-label label-input ${props.requered ? 'required' : ''}`}
        title={props.hint || props.label}
      >
        {props.label}
        {props.requered && <span className="text-red">*</span>}
        {props.secondLabel && <span className="instrucao-limites">{props.secondLabel}</span>}
        {isValidating && (
          <span className="validating-indicator">
            <i className="fas fa-spinner fa-spin ms-2"></i>
            <span className="sr-only">Validando...</span>
          </span>
        )}
      </label>

      <div className="input-container">
        {renderInput()}
        
        {/* Indicador de campo válido */}
        {formik.touched.valor && !formik.errors.valor && !erroEmail && formik.values.valor && (
          <div className="input-success-indicator">
            <i className="fas fa-check text-success"></i>
          </div>
        )}
      </div>

      {/* Mensagens de erro */}
      {formik.touched.valor && formik.errors.valor && (
        <div id={`${uniqueId}-error`} className="erro-formulario" role="alert">
          <i className="fas fa-exclamation-circle me-1"></i>
          {formik.errors.valor}
        </div>
      )}
      
      {erroEmail && (
        <div id={`${uniqueId}-error`} className="erro-formulario" role="alert">
          <i className="fas fa-exclamation-circle me-1"></i>
          Este e-mail já possui cadastro
        </div>
      )}

      {/* Contador de caracteres para campos com limite */}
      {props.maxLength && (
        <div className="character-counter">
          {(formik.values.valor || '').length} / {props.maxLength}
        </div>
      )}

      {/* Dicas de preenchimento */}
      {props.hint && !formik.touched.valor && (
        <div className="input-hint">
          <i className="fas fa-info-circle me-1"></i>
          {props.hint}
        </div>
      )}
    </div>
  );
};

export default React.memo(InputText);
